/**
 * Peças compartilhadas das telas do financeiro: prévia de parcelas e selo de situação.
 * Puras (sem I/O); o texto sempre acompanha a cor.
 */
import { createElement as h, type ReactElement } from "react";
import { splitInstallments } from "../../../../packages/financial/src/cashflow.js";
import { formatBrl } from "../format.js";
import { Badge, type BadgeTone } from "../ui/index.js";
import { describeDue, dueSituation, formatDate } from "../finance/format.js";

/** "3x de R$ 333,33 · 10/11/2026, 10/12/2026, 10/01/2027" — o operador confere antes de salvar. */
export function InstallmentPreview({ amountCents, installments, firstDueDate, intervalMonths = 1 }: { amountCents: number | null; installments: number; firstDueDate: string; intervalMonths?: number }): ReactElement | null {
  if (amountCents === null || !firstDueDate) return null;
  let plan;
  try {
    plan = splitInstallments(amountCents, installments, firstDueDate, intervalMonths);
  } catch {
    return h("p", { className: "m-0 text-sm text-danger", role: "alert" }, "Confira o valor, o número de parcelas e a data do primeiro vencimento.");
  }
  const first = plan[0];
  const last = plan[plan.length - 1];
  const equal = first.amountCents === last.amountCents;
  return h(
    "p",
    { className: "m-0 text-sm text-muted", "data-installment-preview": String(plan.length) },
    plan.length === 1
      ? `À vista: ${formatBrl(first.amountCents)} em ${formatDate(first.dueDate)}.`
      : `${plan.length}x de ${formatBrl(first.amountCents)}${equal ? "" : ` (última de ${formatBrl(last.amountCents)})`} · ${plan.slice(0, 6).map((p) => formatDate(p.dueDate)).join(", ")}${plan.length > 6 ? "…" : ""}`
  );
}

export function DueBadge({ status, dueDate, today, settledLabel, settledAt }: { status: "open" | "settled" | "cancelled"; dueDate: string; today: string; settledLabel: string; settledAt?: string | null }): ReactElement {
  if (status === "cancelled") return h(Badge, { tone: "neutral" }, "Cancelada");
  if (status === "settled") {
    return h(Badge, { tone: "success", title: settledAt ? `${settledLabel} em ${formatDate(settledAt)}` : undefined }, settledLabel);
  }
  const situation = dueSituation(dueDate, today);
  const tone: BadgeTone = situation.kind === "overdue" ? "danger" : situation.kind === "today" ? "warning" : "info";
  return h(Badge, { tone, "data-due": situation.kind }, describeDue(situation));
}
